import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiBarChart2, FiMessageSquare, FiCloud, FiZap, FiEyeOff, FiPieChart,
  FiThumbsUp, FiCheckCircle, FiPlus, FiSend, FiAward
} = FiIcons;

const PollioTab = () => {
  const { t } = useLanguage();

  // Live Poll Simulator State
  const [pollOptions, setPollOptions] = useState([
    { id: 1, text: 'AI-Powered Concierge & Automations', votes: 168 },
    { id: 2, text: 'Private QR Photo & Video Galleries', votes: 242 },
    { id: 3, text: 'Virtual Stage & Low-Cost Networking Lounges', votes: 94 },
    { id: 4, text: '13-Network Social Multi-Posting', votes: 135 }
  ]);
  const [userVoted, setUserVoted] = useState(null);

  // Live Q&A State
  const [questions, setQuestions] = useState([
    { id: 1, author: 'Sarah K.', text: 'Can attendees submit questions anonymously without creating an account?', upvotes: 42, userUpvoted: false },
    { id: 2, author: 'David L.', text: 'Does Pollio integrate directly into big stage LED screens and live streams?', upvotes: 35, userUpvoted: false }
  ]);
  const [newQuestion, setNewQuestion] = useState('');

  const totalVotes = pollOptions.reduce((sum, opt) => sum + opt.votes, 0);

  const handleVote = (optionId) => {
    if (userVoted === optionId) return;
    setPollOptions(prev => prev.map(opt => {
      if (opt.id === optionId) return { ...opt, votes: opt.votes + 1 };
      if (opt.id === userVoted) return { ...opt, votes: opt.votes - 1 };
      return opt;
    }));
    setUserVoted(optionId);
  };

  const handleUpvote = (qId) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === qId) {
        return {
          ...q,
          upvotes: q.userUpvoted ? q.upvotes - 1 : q.upvotes + 1,
          userUpvoted: !q.userUpvoted
        };
      }
      return q;
    }));
  };

  const handleAddQuestion = (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;
    setQuestions([
      { id: Date.now(), author: 'You (Anonymous)', text: newQuestion, upvotes: 1, userUpvoted: true },
      ...questions
    ]);
    setNewQuestion('');
  };

  const features = [
    {
      icon: FiBarChart2,
      title: t('solutionsPage.pollio.features.f1Title') || "Live Visual Polls",
      description: t('solutionsPage.pollio.features.f1Desc') || "Create multiple-choice or rating polls that update in real-time. Beautifully animated charts keep your audience glued to the screen."
    },
    {
      icon: FiMessageSquare,
      title: t('solutionsPage.pollio.features.f2Title') || "Moderated Q&A",
      description: t('solutionsPage.pollio.features.f2Desc') || "Let guests upvote questions so you address what matters most. Full moderation tools allow you to filter and spotlight key inquiries."
    },
    {
      icon: FiCloud,
      title: t('solutionsPage.pollio.features.f3Title') || "Dynamic Word Clouds",
      description: t('solutionsPage.pollio.features.f3Desc') || "Visualize collective sentiment. Watch the most popular answers grow in size as your audience submits their thoughts simultaneously."
    },
    {
      icon: FiZap,
      title: t('solutionsPage.pollio.features.f4Title') || "Instant Results Sync",
      description: t('solutionsPage.pollio.features.f4Desc') || "Zero latency between submission and display. Whether you have 10 or 10,000 guests, Pollio handles the load with enterprise stability."
    },
    {
      icon: FiEyeOff,
      title: t('solutionsPage.pollio.features.f5Title') || "Anonymous Participation",
      description: t('solutionsPage.pollio.features.f5Desc') || "Boost engagement by allowing guests to vote without revealing their identity, encouraging honest and diverse feedback."
    },
    {
      icon: FiPieChart,
      title: t('solutionsPage.pollio.features.f6Title') || "Post-Event Analytics",
      description: t('solutionsPage.pollio.features.f6Desc') || "Export deep engagement reports. Understand peak interaction times and sentiment shifts throughout your event sessions."
    }
  ];

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-amber-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
          <span>{t('solutionsPage.pollio.badge') || "Real-Time Audience Engagement"}</span>
          <span className="bg-amber-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-xs">{t('solutionsPage.pollio.newPill') || "NEW"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.pollio.title') || "Engage Your Audience in Real-Time"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.pollio.subtitle') || "Real-time visual polling, moderated audience Q&A, and sentiment word clouds."}
        </p>
      </div>

      {/* Interactive Live Polling & Q&A Simulator */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.pollio.sessionTitle') || "Pollio Live Interactive Session"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.pollio.sessionSubtitle') || "Try voting below to see zero-latency live visual sync in action."}</p>
          </div>
          <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold font-mono">
            {totalVotes} {t('solutionsPage.pollio.responsesBadge') || "Audience Responses"}
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Live Visual Poll Column (6 Cols) */}
          <div className="lg:col-span-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('solutionsPage.pollio.pollSectionTitle') || "Live Visual Poll"}</span>
              <span className="text-xs text-amber-600 font-semibold">{t('solutionsPage.pollio.pollSectionHint') || "Click an option to cast vote"}</span>
            </div>

            <div className="p-5 rounded-2xl bg-white/70 border border-slate-200/80 shadow-sm space-y-4">
              <h4 className="text-sm font-bold text-slate-900">
                {t('solutionsPage.pollio.pollQuestion') || "Which event technology will drive the biggest ROI for your upcoming 2026 events?"}
              </h4>

              <div className="space-y-3">
                {pollOptions.map((opt) => {
                  const percent = Math.round((opt.votes / totalVotes) * 100);
                  const isVoted = userVoted === opt.id;
                  return (
                    <div
                      key={opt.id}
                      onClick={() => handleVote(opt.id)}
                      className={`relative p-3.5 rounded-xl border cursor-pointer transition-all overflow-hidden ${
                        isVoted
                          ? 'border-amber-500 bg-amber-50/40 ring-1 ring-amber-500/20 shadow-sm'
                          : 'border-slate-200/80 bg-white hover:border-slate-300'
                      }`}
                    >
                      {/* Animated Progress Fill */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/15"
                        initial={{ width: 0 }}
                        animate={{ width: `${percent}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />

                      <div className="relative flex items-center justify-between text-xs z-10">
                        <span className={`font-semibold ${isVoted ? 'text-amber-900 font-bold' : 'text-slate-800'}`}>
                          {opt.text}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-slate-900">{percent}%</span>
                          <span className="text-[10px] text-slate-400">({opt.votes})</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Moderated Q&A Column (6 Cols) */}
          <div className="lg:col-span-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('solutionsPage.pollio.qaTitle') || "Moderated Stage Q&A"}</span>
              <span className="text-xs text-slate-500 font-medium">{t('solutionsPage.pollio.sortedUpvotes') || "Sorted by Upvotes"}</span>
            </div>

            <div className="space-y-3">
              {questions.map((q) => (
                <div
                  key={q.id}
                  className="p-4 rounded-2xl bg-white/70 border border-slate-200/80 shadow-sm flex items-start justify-between gap-4"
                >
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-900 leading-snug">{q.text}</p>
                    <span className="text-[10px] text-slate-400 font-medium">Asked by {q.author}</span>
                  </div>

                  <button
                    onClick={() => handleUpvote(q.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                      q.userUpvoted
                        ? 'bg-amber-500 text-white border-amber-600 shadow-sm'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-amber-50 hover:text-amber-700'
                    }`}
                  >
                    <SafeIcon icon={FiThumbsUp} className="w-3.5 h-3.5" />
                    <span>{q.upvotes}</span>
                  </button>
                </div>
              ))}
            </div>

            {/* Submit Question */}
            <form onSubmit={handleAddQuestion} className="flex gap-2 pt-1">
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder={t('solutionsPage.pollio.askPlaceholder') || "Ask an anonymous question..."}
                className="flex-1 px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs focus:outline-none focus:border-amber-500 transition-all shadow-xs"
              />
              <button
                type="submit"
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold text-xs shadow-md shadow-amber-500/20 hover:shadow-lg transition-all flex items-center gap-1.5"
              >
                <span>{t('solutionsPage.pollio.askButton') || "Ask"}</span>
                <SafeIcon icon={FiSend} className="w-3 h-3" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* The 6 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-amber-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-600/20 border border-amber-200/60 flex items-center justify-center text-amber-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PollioTab;
